import { useState, useRef, useCallback } from 'react';
import AnnotationPopover from './AnnotationPopover';
import CategoryBadge from './CategoryBadge';

interface Annotation {
  start: number;
  end: number;
  categories: string[];
  note?: string;
}

interface Category {
  id: string;
  group_name: string;
  name: string;
  label: string;
  description: string | null;
  color: string | null;
}

interface AnnotatableTextProps {
  text: string;
  annotations: Annotation[];
  categories: Record<string, Category[]>;
  onAnnotationAdd: (annotation: Annotation) => void;
  onAnnotationRemove: (index: number) => void;
  readonly?: boolean;
}

export function AnnotatableText({
  text,
  annotations,
  categories,
  onAnnotationAdd,
  onAnnotationRemove,
  readonly = false
}: AnnotatableTextProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [selection, setSelection] = useState<{ start: number; end: number } | null>(null);
  const [popoverPosition, setPopoverPosition] = useState({ x: 0, y: 0 });
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

  const allCategories = Object.values(categories).flat();
  const getCategoryById = (id: string) => allCategories.find(c => c.id === id);

  const handleMouseUp = useCallback(() => {
    if (readonly) return;

    const sel = window.getSelection();
    if (!sel || sel.isCollapsed || !containerRef.current) return;

    const range = sel.getRangeAt(0);
    const rangeRect = range.getBoundingClientRect();

    // Calculate text offsets
    const preRange = document.createRange();
    preRange.setStart(containerRef.current, 0);
    preRange.setEnd(range.startContainer, range.startOffset);
    const start = preRange.toString().length;
    const end = start + sel.toString().length;

    if (end > start) {
      setSelection({ start, end });
      setPopoverPosition({
        x: Math.min(rangeRect.left, window.innerWidth - 320),
        y: rangeRect.bottom + 8
      });
      setSelectedCategories([]);
    }
  }, [readonly]);

  const handleConfirmAnnotation = () => {
    if (selection && selectedCategories.length > 0) {
      onAnnotationAdd({
        start: selection.start,
        end: selection.end,
        categories: selectedCategories
      });
      setSelection(null);
      window.getSelection()?.removeAllRanges();
    }
  };

  const handleCancelAnnotation = () => {
    setSelection(null);
    window.getSelection()?.removeAllRanges();
  };

  // Render text with highlights
  const renderText = () => {
    if (annotations.length === 0) {
      return <span>{text}</span>;
    }

    // Sort annotations by start position
    const sorted = [...annotations].sort((a, b) => a.start - b.start);
    const elements: JSX.Element[] = [];
    let lastEnd = 0;

    sorted.forEach((ann, idx) => {
      // Text before annotation
      if (ann.start > lastEnd) {
        elements.push(
          <span key={`text-${idx}`}>{text.slice(lastEnd, ann.start)}</span>
        );
      }

      // Get primary color from first category
      const primaryCat = getCategoryById(ann.categories[0]);
      const color = primaryCat?.color || '#6366f1';

      elements.push(
        <mark
          key={`ann-${idx}`}
          className="cursor-pointer relative group"
          style={{ backgroundColor: `${color}30`, borderBottom: `2px solid ${color}` }}
          onClick={() => !readonly && onAnnotationRemove(idx)}
          title={ann.categories.map(id => getCategoryById(id)?.label).join(', ')}
        >
          {text.slice(ann.start, ann.end)}
          {!readonly && (
            <span className="absolute -top-6 left-0 hidden group-hover:block bg-gray-800 text-xs px-2 py-1 rounded whitespace-nowrap">
              Click to remove
            </span>
          )}
        </mark>
      );

      lastEnd = ann.end;
    });

    // Remaining text
    if (lastEnd < text.length) {
      elements.push(
        <span key="text-end">{text.slice(lastEnd)}</span>
      );
    }

    return elements;
  };

  return (
    <div className="relative">
      <div
        ref={containerRef}
        onMouseUp={handleMouseUp}
        className="bg-gray-800 rounded-lg p-6 text-gray-200 leading-relaxed whitespace-pre-wrap select-text"
      >
        {renderText()}
      </div>

      {selection && (
        <AnnotationPopover
          categories={categories}
          selectedCategories={selectedCategories}
          onSelect={setSelectedCategories}
          onConfirm={handleConfirmAnnotation}
          onCancel={handleCancelAnnotation}
          position={popoverPosition}
        />
      )}

      {/* Annotation summary */}
      {annotations.length > 0 && (
        <div className="mt-4 p-4 bg-gray-800/50 rounded-lg">
          <h4 className="text-sm font-medium text-gray-400 mb-2">
            Your Annotations ({annotations.length})
          </h4>
          <div className="space-y-2">
            {annotations.map((ann, idx) => (
              <div key={idx} className="flex items-start gap-2 text-sm">
                <span className="text-gray-500 shrink-0">#{idx + 1}</span>
                <span className="text-gray-400 truncate max-w-xs">
                  "{text.slice(ann.start, Math.min(ann.end, ann.start + 50))}..."
                </span>
                <div className="flex gap-1 flex-wrap">
                  {ann.categories.map(catId => {
                    const cat = getCategoryById(catId);
                    return cat ? (
                      <CategoryBadge
                        key={catId}
                        name={cat.name}
                        label={cat.label}
                        color={cat.color || '#6366f1'}
                      />
                    ) : null;
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default AnnotatableText;
